import { Check, Plus } from 'lucide-react';
import type { SubscriptionPlan } from '../../types/subscription';
import { TRIAL_DAY_OPTIONS, newTrialDraft, type TrialDraft } from './trialDrafts';
import subscriptionService from '../../services/subscription';

/**
 * Pick modules to trial, each with a length and seat counts. Tapping a module
 * adds it with 30 days and one seat on its first tier, so the common case at
 * a trade show is a tap per module and nothing else.
 */
export default function TrialPlanPicker({ plans, drafts, onChange, excludePlanIds = [] }: {
  plans: SubscriptionPlan[];
  drafts: Record<number, TrialDraft>;
  onChange: (next: Record<number, TrialDraft>) => void;
  excludePlanIds?: number[];
}) {
  const visible = plans.filter(p => !excludePlanIds.includes(p.id));

  const toggle = (plan: SubscriptionPlan) => {
    const next = { ...drafts };
    if (next[plan.id]) delete next[plan.id];
    else next[plan.id] = newTrialDraft(plan);
    onChange(next);
  };

  const update = (planId: number, patch: Partial<TrialDraft>) =>
    onChange({ ...drafts, [planId]: { ...drafts[planId], ...patch } });

  if (visible.length === 0) {
    return <p className="text-sm text-neutral-500">Every module is already on this organisation.</p>;
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {visible.map(plan => {
        const d = drafts[plan.id];
        return (
          <div key={plan.id} className={`rounded-lg border-2 p-3 ${d ? 'border-primary-400 bg-primary-50/40' : 'border-neutral-200 bg-white'}`}>
            <button
              type="button"
              onClick={() => toggle(plan)}
              className="w-full flex items-start justify-between gap-2 text-left"
              aria-pressed={!!d}
            >
              <span className="min-w-0">
                <span className="block font-medium text-neutral-900">{plan.name}</span>
                <span className="block text-xs text-neutral-500">
                  {subscriptionService.formatCurrency(plan.base_price)}/mo base
                </span>
              </span>
              <span className={`p-1 rounded flex-shrink-0 ${d ? 'bg-primary-600 text-white' : 'bg-neutral-100 text-neutral-500'}`}>
                {d ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
              </span>
            </button>

            {d && (
              <div className="mt-3 pt-3 border-t border-neutral-200 space-y-2">
                <div className="flex flex-wrap gap-1.5" role="group" aria-label={`${plan.name} trial length`}>
                  {TRIAL_DAY_OPTIONS.map(n => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => update(plan.id, { days: n })}
                      className={`px-2.5 py-1 rounded-md text-xs font-medium border ${
                        d.days === n ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-neutral-700 border-neutral-300'
                      }`}
                    >
                      {n} days
                    </button>
                  ))}
                </div>
                {(plan.license_types ?? []).map(lt => (
                  <div key={lt.id} className="flex items-center justify-between gap-2">
                    <label htmlFor={`trial-${plan.id}-${lt.type_key}`} className="text-sm text-neutral-700 min-w-0 break-words">
                      {lt.name}
                    </label>
                    <input
                      id={`trial-${plan.id}-${lt.type_key}`}
                      type="number"
                      min={0}
                      value={d.seats[lt.type_key] ?? 0}
                      onChange={e => update(plan.id, { seats: { ...d.seats, [lt.type_key]: Math.max(0, parseInt(e.target.value) || 0) } })}
                      className="w-16 flex-shrink-0 text-center border border-neutral-300 rounded px-1 py-1 text-sm"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
